import type { HomeCommand, HomeState } from "@home-control/shared";

export type PendingCommands = Record<string, HomeCommand>;

export function applyPendingCommands(state: HomeState, pending: PendingCommands): HomeState {
  return Object.values(pending).reduce((nextState, command) => applyOptimisticCommand(nextState, command), state);
}

export function verifiedPendingLabels(state: HomeState, pending: PendingCommands): string[] {
  return Object.entries(pending)
    .filter(([, command]) => commandMatchesState(state, command))
    .map(([label]) => label);
}

export function commandMatchesState(state: HomeState, command: HomeCommand): boolean {
  switch (command.kind) {
    case "setThermostatTarget":
      return state.thermostats[command.thermostat].targetTempF === command.targetTempF;
    case "setThermostatMode":
      return state.thermostats[command.thermostat].mode === command.mode;
    case "setMiniSplitTarget":
      return state.miniSplit.targetTempF === command.targetTempF;
    case "setMiniSplitMode":
      return state.miniSplit.mode === command.mode;
    case "setMiniSplitFan":
      return state.miniSplit.fan === command.fan;
    case "setCircuitState":
      return state.pool.circuits[command.circuit].on === command.on;
    case "setPoolSetPoint":
      return state.pool.heaters[command.heater].setPointF === command.setPointF;
    case "setPoolHeatMode":
      return state.pool.heaters[command.heater].heatMode === command.heatMode;
    case "setSchedule": {
      const schedule = state.schedules.find((entry) => entry.id === command.schedule.id);
      return schedule !== undefined && sameSchedule(schedule, command.schedule);
    }
    case "deleteSchedule":
      return !state.schedules.some((entry) => entry.id === command.id);
    case "setPlugState":
      return state.plugs?.[command.plug]?.on === command.on;
    case "cleanLitterBox":
      return state.litterBox.lastCleanRequestedAt === command.requestedAt;
    case "playRadio":
      return state.music?.playing === true && state.music?.stationId === command.station;
    case "searchMusicArtists":
      // A search changes no state; report it verified immediately so it never hangs pending.
      return true;
    case "playMusicUri":
      return state.music?.playing === true;
    case "pauseMusic":
      return state.music?.paused === true;
    case "resumeMusic":
      return state.music?.playing === true;
    case "stopMusic":
      return state.music?.playing === false && state.music?.paused === false;
    case "nextTrack":
    case "previousTrack":
      // The target track isn't knowable from state; settle immediately like a search.
      return true;
    case "playRadioFromCurrent":
      return state.music?.playing === true;
    case "likeCurrentTrack":
      return state.music?.liked === true;
    case "setMusicPlayer":
      return state.music?.player === command.player;
    case "setMusicVolume":
      return state.music?.volume === command.volume;
    case "setTeslaClimate":
      return state.tesla?.climateOn === command.enabled;
    case "setTeslaCabinTarget":
      return state.tesla?.driverTempSettingF === command.targetTempF;
    case "setTeslaCabinTo70AndVent":
      return state.tesla?.climateOn === true && state.tesla?.driverTempSettingF === 70;
    case "ventTeslaWindows":
      return state.tesla?.windowsOpen === true;
    case "closeTeslaWindows":
      return state.tesla?.windowsOpen === false;
    case "wakeTesla":
      return state.tesla?.online === true;
    case "setTeslaLock":
      return state.tesla?.locked === command.locked;
    case "flashTeslaLights":
    case "honkTeslaHorn":
    case "openTeslaChargePort":
    case "openTeslaFrunk":
    case "openTeslaTrunk":
    case "closeTeslaTrunk":
      // Tesla state can lag well past the poll window for these, and iOS treats the
      // accepted command result as confirmation. Match that instead of stranding the
      // button in a "not confirmed" toast.
      return true;
  }
}

function sameSchedule(left: HomeState["schedules"][number], right: HomeState["schedules"][number]): boolean {
  const canonicalActions = (actions: typeof left.startActions) => [...actions]
    .map((action) => JSON.stringify(action))
    .sort()
    .join("|");
  return left.name === right.name
    && left.enabled === right.enabled
    && left.startMinutes === right.startMinutes
    && left.endMinutes === right.endMinutes
    && [...left.days].sort().join(",") === [...right.days].sort().join(",")
    && canonicalActions(left.startActions) === canonicalActions(right.startActions)
    && canonicalActions(left.endActions) === canonicalActions(right.endActions);
}

export function applyOptimisticCommand(state: HomeState, command: HomeCommand): HomeState {
  switch (command.kind) {
    case "setThermostatTarget":
      return {
        ...state,
        thermostats: {
          ...state.thermostats,
          [command.thermostat]: {
            ...state.thermostats[command.thermostat],
            targetTempF: command.targetTempF
          }
        }
      };
    case "setThermostatMode":
      return {
        ...state,
        thermostats: {
          ...state.thermostats,
          [command.thermostat]: {
            ...state.thermostats[command.thermostat],
            mode: command.mode
          }
        }
      };
    case "setMiniSplitTarget":
      return {
        ...state,
        miniSplit: {
          ...state.miniSplit,
          targetTempF: command.targetTempF
        }
      };
    case "setMiniSplitMode":
      return {
        ...state,
        miniSplit: {
          ...state.miniSplit,
          mode: command.mode
        }
      };
    case "setMiniSplitFan":
      return {
        ...state,
        miniSplit: {
          ...state.miniSplit,
          fan: command.fan
        }
      };
    case "setCircuitState":
      return {
        ...state,
        pool: {
          ...state.pool,
          circuits: {
            ...state.pool.circuits,
            [command.circuit]: {
              ...state.pool.circuits[command.circuit],
              on: command.on
            }
          }
        }
      };
    case "setPoolSetPoint":
      return {
        ...state,
        pool: {
          ...state.pool,
          heaters: {
            ...state.pool.heaters,
            [command.heater]: {
              ...state.pool.heaters[command.heater],
              setPointF: command.setPointF
            }
          }
        }
      };
    case "setSchedule": {
      const existing = state.schedules.some((entry) => entry.id === command.schedule.id);
      return {
        ...state,
        schedules: existing
          ? state.schedules.map((entry) => (entry.id === command.schedule.id ? command.schedule : entry))
          : [...state.schedules, command.schedule]
      };
    }
    case "deleteSchedule":
      return {
        ...state,
        schedules: state.schedules.filter((entry) => entry.id !== command.id)
      };
    case "setPoolHeatMode":
      return {
        ...state,
        pool: {
          ...state.pool,
          heaters: {
            ...state.pool.heaters,
            [command.heater]: {
              ...state.pool.heaters[command.heater],
              heatMode: command.heatMode
            }
          }
        }
      };
    case "setPlugState":
      return {
        ...state,
        plugs: {
          ...state.plugs,
          [command.plug]: {
            ...state.plugs?.[command.plug],
            id: command.plug,
            label: state.plugs?.[command.plug]?.label ?? "Office lamp",
            online: state.plugs?.[command.plug]?.online ?? false,
            on: command.on
          }
        }
      };
    case "cleanLitterBox":
      return {
        ...state,
        litterBox: {
          ...state.litterBox,
          lastCleanRequestedAt: command.requestedAt
        }
      };
    case "playRadio":
      return {
        ...state,
        music: state.music
          ? { ...state.music, playing: true, paused: false, stationId: command.station }
          : state.music
      };
    case "searchMusicArtists":
      // A search changes no state.
      return state;
    case "playMusicUri":
      return {
        ...state,
        music: state.music
          ? { ...state.music, playing: true, paused: false, stationId: undefined }
          : state.music
      };
    case "pauseMusic":
      return {
        ...state,
        music: state.music ? { ...state.music, playing: false, paused: true } : state.music
      };
    case "resumeMusic":
      return {
        ...state,
        music: state.music ? { ...state.music, playing: true, paused: false } : state.music
      };
    case "stopMusic":
      return {
        ...state,
        music: state.music ? { ...state.music, playing: false, paused: false } : state.music
      };
    case "nextTrack":
    case "previousTrack":
      return state;
    case "playRadioFromCurrent":
      return {
        ...state,
        music: state.music
          ? { ...state.music, playing: true, paused: false, stationId: undefined }
          : state.music
      };
    case "likeCurrentTrack":
      return {
        ...state,
        music: state.music ? { ...state.music, liked: true } : state.music
      };
    case "setMusicPlayer":
      return {
        ...state,
        music: state.music ? { ...state.music, player: command.player } : state.music
      };
    case "setMusicVolume":
      return {
        ...state,
        music: state.music ? { ...state.music, volume: command.volume } : state.music
      };
    case "setTeslaClimate":
      return {
        ...state,
        tesla: state.tesla ? { ...state.tesla, climateOn: command.enabled } : state.tesla
      };
    case "setTeslaCabinTarget":
      return {
        ...state,
        tesla: state.tesla ? { ...state.tesla, driverTempSettingF: command.targetTempF } : state.tesla
      };
    case "setTeslaCabinTo70AndVent":
      return {
        ...state,
        tesla: state.tesla
          ? { ...state.tesla, climateOn: true, driverTempSettingF: 70, windowsOpen: true }
          : state.tesla
      };
    case "ventTeslaWindows":
      return {
        ...state,
        tesla: state.tesla ? { ...state.tesla, windowsOpen: true } : state.tesla
      };
    case "closeTeslaWindows":
      return {
        ...state,
        tesla: state.tesla ? { ...state.tesla, windowsOpen: false } : state.tesla
      };
    case "wakeTesla":
      return {
        ...state,
        tesla: state.tesla ? { ...state.tesla, online: true } : state.tesla
      };
    case "setTeslaLock":
      return {
        ...state,
        tesla: state.tesla ? { ...state.tesla, locked: command.locked } : state.tesla
      };
    case "flashTeslaLights":
    case "honkTeslaHorn":
    case "openTeslaChargePort":
    case "openTeslaFrunk":
    case "openTeslaTrunk":
    case "closeTeslaTrunk":
      return state;
  }
}
